import { SettingsIcon, UserIcon } from "@hopper-ui/icons";
import {
    type ModuleRegisterFunction,
    NestedErrorBoundary,
    ProtectSidebarBottomItemPriority,
    ProtectUserMenuItemPriority,
    PublicLayout,
    getLegacyHomePaths,
    registerProtectDevTool,
    registerProtectSidebarBottomItem,
    registerProtectUserMenuItem,
    shouldShowDevTools
} from "@sg-protect/shell";
import { lazy } from "react";
import { Outlet } from "react-router";
import { ModuleAuthenticatedProviders } from "./components/module-providers/ModuleAuthenticatedProviders.tsx";
import { ModuleGlobalProviders } from "./components/module-providers/ModuleGlobalProviders.tsx";
import { registerPrivateRoutes, registerPublicRoutes } from "./routes.tsx";

const registerRoutes: ModuleRegisterFunction = runtime => {
    runtime.registerRoute({
        element: (
            <ModuleGlobalProviders>
                <Outlet />
            </ModuleGlobalProviders>
        ),
        errorElement: <NestedErrorBoundary />,
        children: [
            {
                element: (
                    <PublicLayout>
                        <Outlet />
                    </PublicLayout>
                ),
                children: registerPublicRoutes()
            },
            {
                element: (
                    <ModuleAuthenticatedProviders>
                        <Outlet />
                    </ModuleAuthenticatedProviders>
                ),
                children: registerPrivateRoutes(runtime)
            }
        ]
    });
};

const registerNavigationItems: ModuleRegisterFunction = () => {
    return (deferredRuntime, { currentWorkspace }) => {
        if (!currentWorkspace?.id) {
            return;
        }

        const homePaths = getLegacyHomePaths(currentWorkspace.id);

        registerProtectSidebarBottomItem(deferredRuntime, {
            id: "workspace-settings",
            label: "Workspace settings",
            to: homePaths.workspaceOverview,
            icon: <SettingsIcon size="md" />,
            priority: ProtectSidebarBottomItemPriority.WorkspaceSettings
        });

        registerProtectUserMenuItem(deferredRuntime, {
            id: "account",
            label: "Account",
            to: homePaths.account,
            icon: <UserIcon />,
            priority: ProtectUserMenuItemPriority.Account
        });
    };
};

const HOME_DEV_TOOL_ID = "home-dev-tool";
function registerDevTools() {
    if (shouldShowDevTools) {
        const HomeDevTool = lazy(() => import("./dev-tools/HomeDevTool.tsx"));
        registerProtectDevTool({
            id: HOME_DEV_TOOL_ID,
            component: HomeDevTool
        });
    }
}

export const register: ModuleRegisterFunction = async runtime => {
    if (runtime.isMswEnabled) {
        const { handlers } = await import("./api/msw.ts");

        // Tags handlers so DUMP_MSW_MOCKS=true (pnpm dev-msw:dump) can report which module each mock came from.
        handlers.forEach(handler => Object.assign(handler, { mockModule: "home" }));

        runtime.registerRequestHandlers(handlers);
    }

    await registerRoutes(runtime);
    registerDevTools();
    return registerNavigationItems(runtime);
};
